import { type RulePackage, type RulePackageFields } from "@api/rest/types-and-hooks";
import {
  DETAIL_PAGE_QA_TAB,
  PackageSource,
  PackageType,
  QARulesParam,
  QA_RULES_PATH,
  ReportCategory,
  TurnedOffBy,
} from "@pages/QARules/constants";
import { ReportType } from "@pages/Templates/constants";

type BadgeAppearance = "success" | "error" | "warn" | "neutral" | "ai" | "processing";

export const PACKAGE_TYPE_BADGE: Record<PackageType, { label: string; appearance: BadgeAppearance }> = {
  [PackageType.BASE]: { label: "Base", appearance: "neutral" },
  [PackageType.INSURANCE]: { label: "Insurance", appearance: "processing" },
  [PackageType.CUSTOM]: { label: "Custom", appearance: "ai" },
};

export const PACKAGE_TYPE_DESCRIPTION: Record<PackageType, string> = {
  [PackageType.BASE]: "Applies to every report",
  [PackageType.INSURANCE]: "Applies when the report's template has one of these payors",
  [PackageType.CUSTOM]: "Applies only where attached to an org or template",
};

export const packageTypeOf = (value: string): PackageType =>
  Object.values(PackageType).find((t) => t === value) ?? PackageType.CUSTOM;

export const SOURCE_LABEL: Record<PackageSource, string> = {
  [PackageSource.BASE]: "Base",
  [PackageSource.INSURANCE]: "Insurance (template payor)",
  [PackageSource.ORGANIZATION]: "Attached to org",
  [PackageSource.TEMPLATE]: "Attached to template",
};

export const TURNED_OFF_LABEL: Record<TurnedOffBy, string> = {
  [TurnedOffBy.ORGANIZATION_PACKAGE]: "Package turned off for this org",
  [TurnedOffBy.TEMPLATE_PACKAGE]: "Package turned off for this template",
  [TurnedOffBy.ORGANIZATION_RULE]: "Rule turned off for this org",
  [TurnedOffBy.TEMPLATE_RULE]: "Rule turned off for this template",
};

export const humanizeEnum = (value: string): string =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/** Link to a package's page, optionally focused on one of its rules. */
export const packageHref = (packageId: number, ruleId?: number): string => {
  const path = `${QA_RULES_PATH}/packages/${packageId}`;
  return ruleId ? `${path}?${new URLSearchParams({ [QARulesParam.RULE]: String(ruleId) })}` : path;
};

export const orgQaHref = (organizationId: number): string =>
  `/organizations/${organizationId}?tab=${DETAIL_PAGE_QA_TAB}`;

export const templateQaHref = (reportTemplateId: number): string =>
  `/templates/${reportTemplateId}?tab=${DETAIL_PAGE_QA_TAB}`;

/** Where a package is being viewed from: an org detail page or a template detail page. */
export interface PackageOwnerContext {
  organizationId?: number;
  reportTemplateId?: number;
}

/**
 * A package's content can be edited from an org / template page only when that org or
 * template is its sole user; shared packages are edited on /qa-rules.
 */
export const canEditInPlace = (
  pkg: Pick<RulePackage, "exclusive_organization_id" | "exclusive_report_template_id"> | undefined,
  context: PackageOwnerContext,
): boolean => {
  if (!pkg) return false;
  if (context.organizationId !== undefined) return pkg.exclusive_organization_id === context.organizationId;
  if (context.reportTemplateId !== undefined) return pkg.exclusive_report_template_id === context.reportTemplateId;
  return false;
};

/** Flattens a DRF error body ({detail}, {non_field_errors: [...]}, {field: [...]}) into one line. */
export const apiErrorMessage = (data: unknown, fallback: string): string => {
  if (!data || typeof data !== "object") return fallback;
  const messages = Object.values(data as Record<string, unknown>).flatMap((value) => {
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === "string") return [value];
    return [];
  });
  return messages.length ? messages.join(" ") : fallback;
};

/** The custom fetcher resolves on every status; throw on non-2xx so callers can catch once. */
export const ensureOk = <T extends { status: number; data: unknown }>(result: T, fallback: string): T => {
  if (result.status < 200 || result.status >= 300) throw new Error(apiErrorMessage(result.data, fallback));
  return result;
};

const ABA_REPORT_TYPES: string[] = [ReportType.INITIAL_ASSESSMENT, ReportType.REASSESSMENT];
const DIAGNOSIS_REPORT_TYPES: string[] = [ReportType.DIAGNOSIS_EVALUATION];

const sameTypes = (a: string[], b: string[]) => a.length === b.length && a.every((t) => b.includes(t));

export const reportCategoryOf = (reportTypes: string[]): ReportCategory => {
  if (sameTypes(reportTypes, ABA_REPORT_TYPES)) return ReportCategory.ABA;
  if (sameTypes(reportTypes, DIAGNOSIS_REPORT_TYPES)) return ReportCategory.DIAGNOSIS;
  return ReportCategory.ALL;
};

export const reportTypesFor = (category: ReportCategory): string[] => {
  if (category === ReportCategory.ABA) return [...ABA_REPORT_TYPES];
  if (category === ReportCategory.DIAGNOSIS) return [...DIAGNOSIS_REPORT_TYPES];
  return [];
};

export const REPORT_CATEGORY_BADGE: Record<ReportCategory, { label: string; appearance: BadgeAppearance } | null> = {
  [ReportCategory.ALL]: null,
  [ReportCategory.ABA]: { label: "ABA", appearance: "success" },
  [ReportCategory.DIAGNOSIS]: { label: "Diagnosis", appearance: "warn" },
};

/** A rule as exported to / imported from JSON. */
export interface RuleJson {
  name?: string;
  category?: string;
  report_types?: string[];
  location?: string;
  scan_for?: string;
  feedback?: string;
  /** QA package manager (v2) exports: `rule` is the name, `instructions` the text. */
  rule?: string;
  instructions?: string;
}

/** A package as exported to / imported from JSON. Accepts the QA package manager's shape too. */
export interface PackageJson {
  name: string;
  description?: string;
  instructions?: string;
  package_type?: string;
  payor_types?: string[];
  report_types?: string[];
  /** QA package manager (v2) exports; imported as archived. */
  is_globally_disabled?: boolean;
  rules?: RuleJson[];
}

export const packageToJson = (pkg: RulePackage): PackageJson => ({
  name: pkg.name,
  description: pkg.description,
  instructions: pkg.instructions,
  package_type: pkg.package_type,
  payor_types: pkg.payor_types,
  report_types: pkg.report_types,
  rules: pkg.rules
    .filter((rule) => !rule.archived_at)
    .map((rule) => ({
      name: rule.name,
      category: rule.category,
      report_types: rule.report_types,
      location: rule.location,
      scan_for: rule.scan_for,
      feedback: rule.feedback,
    })),
});

export const ruleInputFromJson = (rule: RuleJson) => ({
  name: rule.name ?? rule.rule ?? "",
  category: rule.category ?? "",
  report_types: rule.report_types ?? [],
  location: rule.location ?? "",
  scan_for: rule.scan_for ?? rule.instructions ?? "",
  feedback: rule.feedback ?? "",
});

/** Parses pasted JSON (one package or a list). Throws with a readable message. */
export const parsePackagesJson = (json: string): PackageJson[] => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error("Invalid JSON — paste a single package object or an array of packages.");
  }
  const list = Array.isArray(parsed) ? parsed : [parsed];
  list.forEach((item, i) => {
    if (!item || typeof item !== "object" || typeof (item as PackageJson).name !== "string") {
      throw new Error(`Package ${i + 1} is missing a name.`);
    }
  });
  return list as PackageJson[];
};

export const packageFieldsFromJson = (pkg: PackageJson): RulePackageFields => ({
  name: pkg.name,
  description: pkg.description ?? "",
  instructions: pkg.instructions ?? "",
  package_type: packageTypeOf(pkg.package_type ?? PackageType.CUSTOM),
  payor_types: pkg.payor_types ?? [],
  report_types: pkg.report_types ?? [],
});
